//PDATallyGS1BarcodeRules - per-customer override for how a tally barcode is read.
//When the setting is blank the standard GS1 mapping in Gs1Parser is used instead.
//
//Format: semicolon (or newline) separated pairs. Either orientation is accepted -
//"what to post to", or "where to read from" - whichever side names a known field wins:
//
//    3103=TallyQty;21=TallySerialNo;17=UseByDate;13=MfgDate
//    weight=3103;serial=21;useby=17;mfg=13
//
//  field  : weight | serial | useby | mfg | gtin | batch  (case-insensitive)
//  source : a GS1 application identifier (2-4 digits), or a fixed slice written
//           as @start,length using 0-based character positions, e.g. weight=@28,6
//
//A weight taken from a measurement AI (310n) uses the decimal count in the AI.
//A weight taken from a fixed slice, or from an AI without one, can be given an
//explicit decimal count with a trailing /n, e.g. weight=@28,6/3

import { Gs1Barcode, extractAis, isMeasurementAi, measurementDecimals, parseGs1Date } from "./Gs1Parser";

type FieldName = "weight" | "serial" | "useby" | "mfg" | "gtin" | "batch";

interface FieldRule {
  ai?: string;
  start?: number;
  length?: number;
  decimals?: number;
}

const fieldAliases: { [alias: string]: FieldName } = {
  weight: "weight",
  netweight: "weight",
  tallyqty: "weight",
  qty: "weight",
  serial: "serial",
  serialno: "serial",
  tallyserialno: "serial",
  useby: "useby",
  usebydate: "useby",
  mfg: "mfg",
  mfgdate: "mfg",
  packdate: "mfg",
  gtin: "gtin",
  batch: "batch",
  lot: "batch",
};

export const parseTallyRules = (rules?: string): { [field: string]: FieldRule } => {
  const parsed: { [field: string]: FieldRule } = {};
  if (!rules || !rules.trim()) {
    return parsed;
  }
  //only ; and newline separate pairs - a comma belongs to the @start,length syntax
  rules
    .split(/[;\n]/)
    .map((part) => part.trim())
    .filter((part) => !!part)
    .forEach((part) => {
      const eq = part.indexOf("=");
      if (eq <= 0) {
        return;
      }
      const left = part.substring(0, eq).trim();
      const right = part.substring(eq + 1).trim();
      if (!left || !right) {
        return;
      }
      //the rules may be written either way round - "what to post to" (3103=TallyQty)
      //or "where to read from" (weight=3103) - so work out which side names the field
      let field = fieldAliases[left.toLowerCase()];
      let source = right;
      if (!field) {
        field = fieldAliases[right.toLowerCase()];
        source = left;
      }
      if (!field || !source) {
        return;
      }
      //optional explicit decimal count, e.g. @28,6/3 or 3103/3
      let decimals: number | undefined;
      const slash = source.lastIndexOf("/");
      if (slash > 0) {
        const dec = parseInt(source.substring(slash + 1), 10);
        if (!isNaN(dec)) {
          decimals = dec;
        }
        source = source.substring(0, slash);
      }
      if (source.startsWith("@")) {
        const [startStr, lenStr] = source.substring(1).split(",");
        const start = parseInt(startStr, 10);
        const length = parseInt(lenStr, 10);
        if (!isNaN(start) && !isNaN(length)) {
          parsed[field] = { start, length, decimals };
        }
        return;
      }
      if (/^\d{2,4}$/.test(source)) {
        parsed[field] = { ai: source, decimals };
      }
    });
  return parsed;
};

export const hasTallyRules = (rules?: string): boolean => {
  return Object.keys(parseTallyRules(rules)).length > 0;
};

const readValue = (barcode: string, ais: { [ai: string]: string }, rule: FieldRule): string | undefined => {
  if (rule.ai) {
    return ais[rule.ai];
  }
  if (rule.start !== undefined && rule.length !== undefined) {
    return barcode.substr(rule.start, rule.length);
  }
  return undefined;
};

//applies PDATallyGS1BarcodeRules to a barcode; fields the rules do not mention are left unset
export const parseGs1WithRules = (barcode: string, rules: string): Gs1Barcode => {
  const result: Gs1Barcode = { raw: barcode };
  const parsedRules = parseTallyRules(rules);
  const ais = extractAis(barcode);

  Object.keys(parsedRules).forEach((field) => {
    const rule = parsedRules[field];
    const value = readValue(barcode, ais, rule);
    if (value === undefined || value === "") {
      return;
    }
    switch (field as FieldName) {
      case "weight": {
        if (!/^\d+$/.test(value)) {
          return;
        }
        let decimals = rule.decimals;
        if (decimals === undefined && rule.ai && isMeasurementAi(rule.ai)) {
          decimals = measurementDecimals(rule.ai);
        }
        result.netWeight = parseInt(value, 10) / Math.pow(10, decimals || 0);
        break;
      }
      case "serial":
        result.serialNo = value;
        break;
      case "useby":
        result.useByDate = parseGs1Date(value);
        break;
      case "mfg":
        result.mfgDate = parseGs1Date(value);
        break;
      case "gtin":
        result.gtin = value;
        break;
      case "batch":
        result.batch = value;
        break;
      default:
        break;
    }
  });

  return result;
};
